import React, { useState, useCallback, useContext, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
	Button,
	IconButton,
	Paper,
	Menu,
	MenuItem,
	Box,
	Typography,
	Chip,
	Grid,
	ButtonGroup,
} from "@material-ui/core";
import {
	Edit,
	DeleteOutline,
	Refresh,
	MoreVert,
	ArrowForward,
	CheckCircle,
	ArrowDropDown,
	Build,
	LinkOff,
	Star,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";

import api from "../../services/api";
import { useTimezone } from "../../hooks/useTimezone";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import ReconnectProgressModal from "../../components/ReconnectProgressModal";
import DisconnectProgressModal from "../../components/DisconnectProgressModal";
import DeleteWhatsAppModal from "../../components/DeleteWhatsAppModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { Can } from "../../components/Can";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(2),
		overflowY: "scroll",
		...theme.scrollbarStyles,
		backgroundColor: theme.palette.background.default,
		position: 'relative',
		zIndex: 1,
		border: `1px solid ${theme.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
		borderRadius: 10,
		boxShadow: theme.mode === 'dark'
			? '0 10px 28px rgba(0, 0, 0, 0.28)'
			: '0 10px 28px rgba(15, 23, 42, 0.06)',
	},
	connectionsGrid: {
		marginTop: theme.spacing(1.5),
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(300px, max-content))",
		gap: theme.spacing(2),
		alignItems: "start",
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	connectionCard: {
		backgroundColor: theme.palette.background.paper,
		borderRadius: 16,
		boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
		border: `1px solid ${theme.mode === 'dark' ? '#334155' : '#eef0f5'}`,
		overflow: 'hidden',
		position: 'relative',
		height: '100%',
		width: 'fit-content',
		minWidth: 320,
		maxWidth: 420,
		display: 'flex',
		flexDirection: 'column',
		transition: 'transform 0.2s, box-shadow 0.2s',
		'&:hover': {
			transform: 'translateY(-2px)',
			boxShadow: theme.mode === 'dark'
				? '0 12px 26px rgba(0,0,0,0.32)'
				: '0 12px 26px rgba(15,23,42,0.10)',
		},
	},
	connectionCardConnected: {
		backgroundImage: theme.mode === 'dark'
			? 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, #111827 48%)'
			: 'linear-gradient(135deg, #e8f8ee 0%, #ffffff 52%)',
	},
	cardHeader: {
		background: 'transparent',
		padding: theme.spacing(1.75),
		position: 'relative',
		flex: 1,
	},
	whatsappIcon: {
		width: 42,
		height: 42,
		borderRadius: '50%',
		objectFit: 'cover',
		backgroundColor: '#ffffff',
		border: '1px solid rgba(0,0,0,0.08)',
	},
	avatarWrap: {
		position: 'relative',
		width: 42,
		height: 42,
		marginRight: theme.spacing(1.25),
		flexShrink: 0,
	},
	platformBadge: {
		position: 'absolute',
		right: -5,
		bottom: -5,
		width: 20,
		height: 20,
		borderRadius: '50%',
		backgroundColor: '#fff',
		border: '2px solid #fff',
		boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
		objectFit: 'contain',
		padding: 1,
	},
	connectionInfo: {
		flex: 1,
		minWidth: 0,
	},
	connectionName: {
		fontSize: '15px',
		fontWeight: 800,
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(0.25),
		paddingRight: theme.spacing(4),
		whiteSpace: 'normal',
		wordBreak: 'break-word',
	},
	phoneNumber: {
		fontSize: '14px',
		color: '#666',
		marginBottom: theme.spacing(0.5),
	},
	connectionDetail: {
		fontSize: '11.5px',
		color: theme.palette.text.secondary,
		marginBottom: 2,
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(0.5),
	},
	detailLabel: {
		fontWeight: 800,
		minWidth: '86px',
		color: theme.palette.text.primary,
		flexShrink: 0,
	},
	detailValue: {
		flex: 1,
		minWidth: 0,
		whiteSpace: 'pre-wrap',
		overflowWrap: 'anywhere',
		wordBreak: 'break-word',
		maxWidth: 260,
	},
	menuButton: {
		position: 'absolute',
		top: theme.spacing(2),
		right: theme.spacing(2),
		color: '#666',
	},
	statusBadge: {
		marginTop: theme.spacing(1),
		height: '24px',
		fontSize: '12px',
		fontWeight: 'bold',
		borderRadius: '12px',
		padding: '0 12px',
	},
	connectedBadge: {
		backgroundColor: '#ffffff',
		color: '#4caf50',
		border: '1px solid #4caf50',
	},
	pendingBadge: {
		backgroundColor: '#ffffff',
		color: '#ff9800',
		border: '1px solid #ff9800',
	},
	disconnectedBadge: {
		backgroundColor: '#ffffff',
		color: '#f44336',
		border: '1px solid #f44336',
	},
	cardBody: {
		padding: theme.spacing(1.5),
		backgroundColor: theme.mode === 'dark' ? '#111827' : '#f8fafc',
		borderTop: `1px solid ${theme.mode === 'dark' ? '#334155' : '#e5e7eb'}`,
		marginTop: 'auto', // Empuja el body al final de la tarjeta
	},
	actionButton: {
		width: '100%',
		justifyContent: 'space-between',
		padding: theme.spacing(1, 1.5),
		backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff',
		color: theme.palette.text.primary,
		textTransform: 'none',
		fontSize: '13px',
		borderRadius: '8px',
		border: `1px solid ${theme.mode === 'dark' ? '#334155' : '#dbe3ef'}`,
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: theme.mode === 'dark' ? '#263244' : '#eff6ff',
		},
	},
	// ✅ NUEVO: Estilos para badge de conexión predeterminada
	defaultBadge: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(5), // ✅ Ajustado para no solaparse con el menú
		display: 'flex',
		alignItems: 'center',
		backgroundColor: '#ff9800',
		color: '#ffffff',
		padding: '4px 8px',
		borderRadius: '12px',
		fontSize: '10px',
		fontWeight: 'bold',
		boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
		zIndex: 10,
	},
	defaultIcon: {
		fontSize: '14px',
		marginRight: '4px',
	},
	// ✅ TARJETA VACÍA PARA AGREGAR CONEXIÓN
	addConnectionCard: {
		display: "none",
	},
	emptyState: {
		width: "100%",
		minHeight: 420,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
		padding: theme.spacing(4),
	},
	emptyStack: {
		width: 220,
		height: 150,
		position: "relative",
		marginBottom: theme.spacing(3),
	},
	emptyCard: {
		position: "absolute",
		left: "50%",
		width: 160,
		height: 92,
		borderRadius: 14,
		background: "#fff",
		boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
		border: "1px solid #eef0f5",
	},
	emptyTitle: {
		fontSize: 22,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: 6,
	},
	emptySubtitle: {
		fontSize: 14,
		color: theme.palette.text.secondary,
		maxWidth: 360,
	},
}));

const Connections = () => {
	const classes = useStyles();
	const theme = useTheme();
	const history = useHistory();
	const location = useLocation();
	const { formatDateTime } = useTimezone();
	const { user } = useContext(AuthContext);
	const { whatsApps, loading, refreshWhatsApps, dispatch } = useContext(WhatsAppsContext);
	const socketManager = useContext(SocketContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [connectionType, setConnectionType] = useState("whatsapp");
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
	const [reconnectingWhatsApp, setReconnectingWhatsApp] = useState(null);
	const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
	const [disconnectingWhatsApp, setDisconnectingWhatsApp] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deletingWhatsApp, setDeletingWhatsApp] = useState(null);
	// ✅ ESTADO ELIMINADO: autoRefreshInProgress - Causaba bloqueo de la interfaz
	const [lastAutoRefresh, setLastAutoRefresh] = useState(null);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedMenuWhatsApp, setSelectedMenuWhatsApp] = useState(null);
	const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
	// ✅ FASE 2: Estado para alertas de recursos (guardado para uso futuro, ej: dashboard)
	// eslint-disable-next-line no-unused-vars
	const [resourceAlerts, setResourceAlerts] = useState([]);
	
	// ✅ Flag para evitar toasts duplicados de errores QR
	const qrErrorToastShown = useRef(new Map()); // Key: whatsappId-errorCode, Value: timestamp

	// ✅ FASE 2: FUNCIÓN PARA MOSTRAR NOTIFICACIONES DE RECURSOS
	const showResourceNotification = useCallback((alert) => {
		const message = `${alert.message}\n` +
			`Sesiones: ${alert.currentSessions}/${alert.maxTheoretical} (${alert.utilizationPercent.toFixed(1)}%)\n` +
			`Impacto estimado: ~${(alert.impact.memoryMB / 1024).toFixed(1)} GB RAM, ~${alert.impact.cpuPercent.toFixed(1)}% CPU\n` +
			`${alert.impact.recommendation}`;
		
		switch (alert.level) {
			case 'info':
				toast.info(message, { autoClose: 5000 });
				break;
			case 'warning':
				toast.warning(message, { autoClose: 7000 });
				break;
			case 'error':
				toast.error(message, { autoClose: 10000 });
				break;
			case 'critical':
				toast.error(message, {
					autoClose: 15000,
					onClick: () => {
						// Opcional: abrir página de soporte o dashboard
						console.log('Abrir soporte para upgrade de servidor');
					}
				});
				break;
			default:
				toast.info(message, { autoClose: 5000 });
		}
	}, []);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

  // ✅ WEBSOCKET PARA SINCRONIZACIÓN EN TIEMPO REAL
  useEffect(() => {
    if (socketManager) {
      const companyId = localStorage.getItem("companyId");
      const socket = socketManager.getSocket(companyId);

      const handleWhatsAppEvent = (data) => {

        // ✅ Fase 6: Manejar errores de límite de plan
        if (data && data.action === "error" && data.error) {
          if (data.error.code === "PLAN_LIMIT_REACHED") {
            // Mostrar error al usuario
            toast.error(data.error.message || "Has alcanzado el límite de conexiones de tu plan. Elimina conexiones desconectadas o contacta soporte para upgrade.", {
              autoClose: 8000,
            });
            
            // Refrescar conexiones para mostrar estado actualizado
            setTimeout(() => refreshWhatsApps(), 1000);
            
            // Cerrar modal de QR si está abierto
            if (qrModalOpen && data.whatsapp && selectedWhatsApp?.id === data.whatsapp.id) {
              setQrModalOpen(false);
              setSelectedWhatsApp(null);
            }
            
            return; // Salir temprano para no procesar otros eventos
          }
        }
        
        // ⚠️ NOTA: Los errores QR_EXPIRED y MAX_QR_RETRIES_REACHED se manejan SOLO en whatsappSession
        // NO manejar aquí para evitar toast duplicado

        // ✅ Fase 2: Notificar timeout de reconexión si aplica
        if (data && data.action === "update" && data.reason === "RECONNECTION_TIMEOUT" && data.message) {
          if (window.showNotification) {
            window.showNotification(data.message, 'warning');
          } else {
            toast.warning(data.message);
          }
        }

        // ✅ ACTUALIZACIÓN SIMPLIFICADA - Sin bloqueo de interfaz

        if (data.action === "create") {
          // ✅ ACTUALIZACIÓN NO BLOQUEANTE
          setTimeout(() => refreshWhatsApps(), 1000);
        }
        
        if (data.action === "update") {
          // ✅ ACTUALIZACIÓN NO BLOQUEANTE
          setTimeout(() => refreshWhatsApps(), 500);
        }
        
        if (data.action === "delete") {
          // ✅ ACTUALIZACIÓN NO BLOQUEANTE
          setTimeout(() => refreshWhatsApps(), 500);
        }
      };

      const handleWhatsAppSessionEvent = (data) => {
        // ✅ VALIDACIÓN COMPLETA DE DATOS
        if (data && data.action === "update" && data.session && data.session.id) {
          // ✅ MANEJAR ERRORES INFORMATIVOS DE QR (SOLO AQUÍ - NO en whatsapp para evitar duplicados)
          if (data.error) {
            if (data.error.code === "QR_EXPIRED" || data.error.code === "MAX_QR_RETRIES_REACHED") {
              // ✅ Usar flag para evitar toasts duplicados (mismo error en 5 segundos)
              const errorKey = `${data.session.id}-${data.error.code}`;
              const lastShown = qrErrorToastShown.current.get(errorKey);
              const now = Date.now();
              
              // Solo mostrar toast si no se mostró en los últimos 5 segundos
              if (!lastShown || (now - lastShown) > 5000) {
                qrErrorToastShown.current.set(errorKey, now);
                
                // Mostrar mensaje informativo (no error) - SOLO UNA VEZ
                toast.info(data.error.message || "El código QR ha expirado. Por favor, genera un nuevo código QR.", {
                  autoClose: 5000,
                });
              }
              
              // Cerrar modal de QR si está abierto
              if (qrModalOpen && selectedWhatsApp?.id === data.session.id) {
                setQrModalOpen(false);
                setSelectedWhatsApp(null);
              }
              
              // Refrescar conexiones para mostrar estado actualizado
              setTimeout(() => refreshWhatsApps(), 1000);
              return; // Salir temprano
            }
          }
          
          // Refrescar conexiones automáticamente
          refreshWhatsApps();
          
          // ✅ VERIFICAR SI HAY CAMBIO DE ESTADO IMPORTANTE
          const session = data.session;
          if (session.status === "CONNECTED" && session.qrcode === "") {
            
            // ✅ CERRAR MODAL DE QR SI ESTÁ ABIERTO - INMEDIATAMENTE
            if (qrModalOpen && selectedWhatsApp?.id === session.id) {
              setQrModalOpen(false);
              setSelectedWhatsApp(null);
              
              // ✅ ACTUALIZACIÓN INMEDIATA SIN DELAY
              refreshWhatsApps();
              setLastAutoRefresh(Date.now());
            }
            
            // ✅ CERRAR MODAL DE RECONEXIÓN SI ESTÁ ABIERTO - INMEDIATAMENTE
            if (reconnectModalOpen && reconnectingWhatsApp?.id === session.id) {
              setReconnectModalOpen(false);
              setReconnectingWhatsApp(null);
            }
          } else if (session.status === "DISCONNECTED") {
            // ✅ NOTIFICACIÓN DE DESCONEXIÓN ELIMINADA - Evitar spam de notificaciones
          } else if (session.status === "qrcode") {
            // ✅ NOTIFICACIÓN DE QR ELIMINADA - Era redundante durante eliminación
          }
        } else {
          // ✅ MANEJAR DATOS INVÁLIDOS
        }
      };

      const handleWhatsAppConnectionEvent = (data) => {
        // ✅ DETECTAR EVENTO DE CONEXIÓN EXITOSA
        if (data && data.action === "connected" && data.whatsapp && data.whatsapp.id) {
          const whatsapp = data.whatsapp;
          
          // ✅ ACTUALIZAR INMEDIATAMENTE
          refreshWhatsApps();
          
          // ✅ CERRAR MODAL DE QR SI ESTÁ ABIERTO - INMEDIATAMENTE
          if (qrModalOpen && selectedWhatsApp?.id === whatsapp.id) {
            console.log("✅ Conexión detectada - Cerrando modal QR inmediatamente");
            setQrModalOpen(false);
            setSelectedWhatsApp(null);
          }
          
          // ✅ CERRAR MODAL DE RECONEXIÓN SI ESTÁ ABIERTO - INMEDIATAMENTE
          if (reconnectModalOpen && reconnectingWhatsApp?.id === whatsapp.id) {
            console.log("✅ Conexión detectada - Cerrando modal de reconexión inmediatamente");
            setReconnectModalOpen(false);
            setReconnectingWhatsApp(null);
          }
        }
        
        // ✅ DETECTAR EVENTO DE RECONEXIÓN DESPUÉS DE PÉRDIDA DE RED
        if (data && data.action === "reconnected" && data.whatsapp && data.whatsapp.id) {
          const whatsapp = data.whatsapp;
          
          // ✅ NOTIFICAR AL USUARIO QUE LA RED REGRESÓ Y LA CONEXIÓN SE RESTABLECIÓ
          if (window.showSuccessNotification) {
            window.showSuccessNotification(data.message || `🌐 Conexión restablecida - WhatsApp ${whatsapp.name || whatsapp.id} conectado nuevamente`);
          } else {
            toast.success(data.message || `🌐 Conexión restablecida - WhatsApp ${whatsapp.name || whatsapp.id} conectado nuevamente`);
          }
          
          // ✅ ACTUALIZAR INMEDIATAMENTE
          refreshWhatsApps();
          
          // ✅ CERRAR MODAL DE RECONEXIÓN SI ESTÁ ABIERTO - INMEDIATAMENTE
          if (reconnectModalOpen && reconnectingWhatsApp?.id === whatsapp.id) {
            console.log("🌐 Reconexión después de pérdida de red detectada - Cerrando modal de reconexión");
            setReconnectModalOpen(false);
            setReconnectingWhatsApp(null);
          }
        }
      };

      const handleSocketConnect = () => {
        // ✅ REDUCIR SPAM DE LOGS - Solo log en desarrollo
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
          console.log("🔄 Connections: Socket reconectado, re-suscribiendo...");
        }
        
        // ✅ VERIFICAR SI HAY WHATSAPP DESCONECTADOS QUE DEBEN ESTAR RECONECTADOS
        // Refrescar lista para sincronizar estado después de reconexión de red
        setTimeout(() => {
          refreshWhatsApps();
        }, 1000); // Dar tiempo al backend para sincronizar
        
        // ✅ RE-SUSCRIBIRSE A LOS CANALES DESPUÉS DE RECONEXIÓN
        socket.emit("joinNotification");
      };

      const handleSocketDisconnect = (reason) => {
        console.log("⚠️ Connections: Socket desconectado:", reason);
        // ✅ NO mostrar notificación aquí - puede ser temporal y molesta al usuario
        // El backend se encargará de notificar cuando WhatsApp se reconecte
      };

      // ✅ ESCUCHAR EVENTOS DE WHATSAPP Y SESIÓN
      socket.on(`company-${companyId}-whatsapp`, handleWhatsAppEvent);
      socket.on(`company-${companyId}-whatsappSession`, handleWhatsAppSessionEvent);
      socket.on(`company-${companyId}-whatsapp`, handleWhatsAppConnectionEvent);
      socket.on("connect", handleSocketConnect);
      socket.on("disconnect", handleSocketDisconnect);

      // ✅ LIMPIAR LISTENERS AL DESMONTAR
      return () => {
        if (socket && typeof socket.off === 'function') {
          socket.off(`company-${companyId}-whatsapp`, handleWhatsAppEvent);
          socket.off(`company-${companyId}-whatsappSession`, handleWhatsAppSessionEvent);
          socket.off(`company-${companyId}-whatsapp`, handleWhatsAppConnectionEvent);
          socket.off("connect", handleSocketConnect);
          socket.off("disconnect", handleSocketDisconnect);
        }
      };
    }
  }, [socketManager, refreshWhatsApps, qrModalOpen, selectedWhatsApp, reconnectModalOpen, reconnectingWhatsApp]);

  // ✅ FUNCIÓN PARA VERIFICAR ESTADO DE CONEXIONES PERIÓDICAMENTE (OPTIMIZADA)
  useEffect(() => {
    // ✅ VERIFICAR ESTADO CADA 120 SEGUNDOS (menos agresivo) PARA MANTENER SINCRONIZACIÓN
    const intervalId = setInterval(async () => {
      try {
        // ✅ SOLO REFRESCAR SI NO ESTÁ CARGANDO Y HAY CONEXIONES
        if (!loading && whatsApps && whatsApps.length > 0) {
          // ✅ LOG DE VERIFICACIÓN PERIÓDICA
          console.log("VERIFICACIÓN PERIÓDICA DE ESTADO DE CONEXIONES...");
          await refreshWhatsApps();
        }
      } catch (error) {
        console.error("Error en verificación periódica:", error);
      }
    }, 120000); // ✅ OPTIMIZADO: 120 segundos (menos agresivo)

    return () => {
      clearInterval(intervalId);
    };
  }, [loading, whatsApps, refreshWhatsApps]);

  // ✅ LISTENER PARA ACTUALIZACIÓN AUTOMÁTICA DESPUÉS DE CONEXIÓN EXITOSA
  useEffect(() => {
    const handleWhatsAppConnectionSuccess = async (event) => {
              console.log("EVENTO DE CONEXIÓN EXITOSA RECIBIDO:", event.detail);
      
      // ✅ ACTUALIZACIÓN SIMPLIFICADA - Sin bloqueo de interfaz
      
      // ✅ VERIFICAR SI YA SE ACTUALIZÓ RECIENTEMENTE (últimos 10 segundos)
      const now = Date.now();
      if (lastAutoRefresh && (now - lastAutoRefresh) < 10000) {
        console.log("ACTUALIZACIÓN AUTOMÁTICA RECIENTE DETECTADA, IGNORANDO...");
        return;
      }
      
      try {
        // ✅ ACTUALIZACIÓN INMEDIATA - Cambiado de 5 segundos a 1 segundo
        setTimeout(async () => {
          // ✅ LOG DE ACTUALIZACIÓN AUTOMÁTICA
          console.log("🚀 EJECUTANDO ACTUALIZACIÓN INMEDIATA DESPUÉS DE CONEXIÓN EXITOSA...");
          
          // ✅ ACTUALIZAR LISTA DE CONEXIONES
          await refreshWhatsApps();
          
          // ✅ ACTUALIZAR TIMESTAMP
          setLastAutoRefresh(Date.now());
          
          // ✅ CONFIRMACIÓN ELIMINADA - Evitar spam de notificaciones
          
        }, 1000); // 1 segundo - ACTUALIZACIÓN INMEDIATA
        
      } catch (error) {
        console.error("Error en actualización automática:", error);
        // ✅ NOTIFICACIÓN DE ERROR ELIMINADA - Evitar spam
      }
    };

    // ✅ AGREGAR LISTENER PARA EL EVENTO PERSONALIZADO
    window.addEventListener('whatsapp-connection-success', handleWhatsAppConnectionSuccess);

    // ✅ LIMPIAR LISTENER AL DESMONTAR
    return () => {
      window.removeEventListener('whatsapp-connection-success', handleWhatsAppConnectionSuccess);
    };
  }, [refreshWhatsApps, lastAutoRefresh]);

  // ✅ LISTENER PARA ACTUALIZACIÓN INMEDIATA DESDE QRCODEMODAL
  useEffect(() => {
    const handleRefreshConnections = async () => {
      console.log("🔄 Evento de actualización de conexiones recibido");
      try {
        // ✅ ACTUALIZACIÓN INMEDIATA SIN DELAY
        await refreshWhatsApps();
        console.log("✅ Conexiones actualizadas inmediatamente");
      } catch (error) {
        console.error("❌ Error actualizando conexiones:", error);
      }
    };

    // ✅ LISTENER PARA ACTUALIZACIÓN DE ESTADO LOCAL INMEDIATA
    const handleUpdateWhatsAppStatus = (event) => {
      const { whatsAppId, newStatus } = event.detail;
      console.log("🔄 Actualizando estado local inmediatamente:", whatsAppId, newStatus);
      
      // ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE
      if (whatsApps && whatsApps.length > 0) {
        const updatedWhatsApps = whatsApps.map(wa => {
          if (wa.id === whatsAppId) {
            return {
              ...wa,
              status: newStatus.status,
              qrcode: newStatus.qrcode,
              updatedAt: newStatus.updatedAt
            };
          }
          return wa;
        });
        
        // ✅ ACTUALIZAR ESTADO LOCAL
        dispatch({ type: "REFRESH_WHATSAPPS", payload: updatedWhatsApps });
        console.log("✅ Estado local actualizado inmediatamente");
      }
    };

    // ✅ ESCUCHAR EVENTOS PERSONALIZADOS
    window.addEventListener('refreshConnections', handleRefreshConnections);
    window.addEventListener('updateWhatsAppStatus', handleUpdateWhatsAppStatus);

    return () => {
      window.removeEventListener('refreshConnections', handleRefreshConnections);
      window.removeEventListener('updateWhatsAppStatus', handleUpdateWhatsAppStatus);
    };
  }, [refreshWhatsApps, whatsApps, dispatch]);

  // ✅ FASE 1C: FUNCIÓN PARA LIMPIAR Y RECONECTAR SESIÓN (mover antes del useEffect)
  const handleCleanAndReconnect = useCallback(async (whatsAppId) => {
    try {
      const whatsapp = whatsApps.find(w => w.id === whatsAppId);
      if (!whatsapp) {
        toast.error("❌ No se encontró la conexión WhatsApp");
        return;
      }

      // ✅ MOSTRAR CONFIRMACIÓN
      const confirmed = window.confirm(
        `¿Desea limpiar la sesión corrupta de "${whatsapp.name}" y reconectar?\n\n` +
        `Esto eliminará:\n` +
        `- Datos de sesión corruptos\n` +
        `- Archivos de sesión antiguos\n` +
        `- Y creará una nueva sesión limpia`
      );

      if (!confirmed) return;

      // ✅ MOSTRAR LOADING
      toast.info("🧹 Limpiando sesión corrupta...", { autoClose: 2000 });

      // ✅ LLAMAR API PARA LIMPIAR SESIÓN
      const response = await api.post(`/whatsapp/${whatsAppId}/clean-session`);

      if (response.status === 200 && response.data.success) {
        toast.success("✅ Sesión limpiada exitosamente. Reconexión iniciada...", { autoClose: 3000 });
        
        // ✅ ACTUALIZAR ESTADO DESPUÉS DE UN DELAY
        setTimeout(async () => {
          try {
            await refreshWhatsApps();
            
            // ✅ VERIFICAR SI NECESITA MOSTRAR QR
            setTimeout(() => {
              const updatedWhatsapp = whatsApps.find(w => w.id === whatsAppId);
              if (updatedWhatsapp && (updatedWhatsapp.status === 'PENDING' || updatedWhatsapp.status === 'DISCONNECTED')) {
                setSelectedWhatsApp(updatedWhatsapp);
                setQrModalOpen(true);
              }
            }, 2000);
          } catch (error) {
            console.error("Error actualizando estado:", error);
          }
        }, 3000);
      } else {
        toast.error("❌ Error al limpiar sesión. Intente nuevamente.");
      }
    } catch (error) {
      console.error("❌ Error limpiando sesión:", error);
      toast.error("❌ Error al limpiar sesión: " + (error.response?.data?.error || error.message));
    }
  }, [whatsApps, refreshWhatsApps]);

  // ✅ LISTENER PARA ACTUALIZACIÓN INMEDIATA DESDE SOCKET
  useEffect(() => {
    if (!socketManager || !user.companyId) return;

    const socket = socketManager.getSocket(user.companyId);
    if (!socket) return;

    const handleSocketWhatsAppUpdate = (data) => {
      // ✅ Fase 6: Manejar errores de límite de plan
      if (data && data.action === "error" && data.error) {
        if (data.error.code === "PLAN_LIMIT_REACHED") {
          // Mostrar error al usuario
          toast.error(data.error.message || "Has alcanzado el límite de conexiones de tu plan. Elimina conexiones desconectadas o contacta soporte para upgrade.", {
            autoClose: 8000,
          });
          
          // Refrescar conexiones para mostrar estado actualizado
          refreshWhatsApps();
          return; // Salir temprano para no procesar otros eventos
        }
      }
      
      // ✅ DETECTAR RECONEXIÓN DESPUÉS DE PÉRDIDA DE RED
      if (data.action === "reconnected" && data.whatsapp) {
        const wa = data.whatsapp;
        refreshWhatsApps();
        if (window.showSuccessNotification) {
          window.showSuccessNotification(data.message || `🌐 Conexión restablecida - WhatsApp ${wa.name || wa.id} conectado nuevamente después de pérdida de red`);
        } else {
          toast.success(data.message || `🌐 Conexión restablecida - WhatsApp ${wa.name || wa.id} conectado nuevamente`);
        }
        return;
      }
      
      // ✅ FASE 1C: DETECTAR badSession Y MOSTRAR NOTIFICACIÓN CRÍTICA
      if (data.action === "disconnected" && data.whatsapp) {
        const wa = data.whatsapp;
        const isBadSession = wa.lastDisconnectReason === "BAD_SESSION" || 
                            wa.lastDisconnectReason === "BAD_SESSION_CLEANUP_FAILED";
        
        if (isBadSession) {
          // ✅ NOTIFICACIÓN CRÍTICA CON OPCIÓN DE ACCIÓN
          const whatsappName = wa.name || `WhatsApp ${wa.id}`;
          toast.error(
            `⚠️ ${whatsappName} desconectado - Sesión corrupta detectada`,
            {
              autoClose: 10000, // 10 segundos
              onClick: () => {
                // Al hacer clic en la notificación, abrir opción de limpiar
                handleCleanAndReconnect(wa.id);
              }
            }
          );
          
          // ✅ ACTUALIZAR UI INMEDIATAMENTE
          refreshWhatsApps();
          return;
        }
      }
      
      // ✅ CORREGIDO: Procesar tanto data.whatsapp como data.session
      const whatsappData = data.whatsapp || data.session;
      
      if (data.action === "update" && whatsappData) {
        // ✅ ACTUALIZAR INMEDIATAMENTE sin importar el estado (para sincronizar siempre)
        refreshWhatsApps();
      }
      
      // ✅ NUEVO: Manejar action "connected" para reconexiones exitosas
      if (data.action === "connected" && whatsappData) {
        refreshWhatsApps();
      }
    };

    // ✅ ESCUCHAR EVENTOS DE WHATSAPP
    socket.on(`company-${user.companyId}-whatsapp`, handleSocketWhatsAppUpdate);

    return () => {
      socket.off(`company-${user.companyId}-whatsapp`, handleSocketWhatsAppUpdate);
    };
  }, [socketManager, user.companyId, refreshWhatsApps, handleCleanAndReconnect, whatsApps]);

  // ✅ FASE 2: LISTENER PARA ALERTAS DE RECURSOS
  useEffect(() => {
    if (!socketManager || !user.companyId) return;
    
    const socket = socketManager.getSocket(user.companyId);
    if (!socket) return;
    
    // ✅ LISTENER PARA ALERTAS DE RECURSOS
    const handleResourceAlert = (data) => {
      if (data.action === 'resource_alert' && data.alert) {
        const alert = data.alert;
        
        // Mantener últimas 5 alertas
        setResourceAlerts(prev => [...prev.slice(-4), alert]);
        
        // Mostrar notificación según nivel
        showResourceNotification(alert);
      }
    };
    
    socket.on(`company-${user.companyId}-resource-alert`, handleResourceAlert);
    
    return () => {
      socket.off(`company-${user.companyId}-resource-alert`, handleResourceAlert);
    };
  }, [socketManager, user.companyId, showResourceNotification]);

  // ✅ FUNCIÓN PARA RECONECTAR SESIÓN WHATSAPP CON POPUP DE PROGRESO
  const handleReconnectWhatsApp = async (whatsAppId) => {
    try {
      console.log(`🔄 Iniciando reconexión para WhatsApp ${whatsAppId}`);
      
      // ✅ ENCONTRAR EL WHATSAPP A RECONECTAR
      const whatsapp = whatsApps.find(w => w.id === whatsAppId);
      if (!whatsapp) {
        toast.error("❌ No se encontró la conexión WhatsApp");
        return;
      }
      
      // ✅ CONFIGURAR ESTADO PARA EL MODAL DE PROGRESO
      setReconnectingWhatsApp(whatsapp);
      setReconnectModalOpen(true);
      
      // ✅ LLAMAR API PARA RECONECTAR (SIN NOTIFICACIONES MÚLTIPLES)
      const response = await api.post(`/whatsapp/${whatsAppId}/restart`);
      
      if (response.status === 200) {
        console.log("✅ Reconexión iniciada exitosamente");
        
        // ✅ ACTUALIZAR ESTADO LOCAL DESPUÉS DE UN DELAY
        setTimeout(async () => {
          try {
            await refreshWhatsApps();
            console.log("✅ Estado actualizado después de reconexión");
          } catch (error) {
            console.error("Error actualizando estado:", error);
          }
        }, 3000);
        
        // ✅ VERIFICAR SI NECESITA MOSTRAR QR DESPUÉS DEL MODAL
        setTimeout(() => {
          const updatedWhatsapp = whatsApps.find(w => w.id === whatsAppId);
          if (updatedWhatsapp && updatedWhatsapp.status === 'PENDING') {
            setSelectedWhatsApp(updatedWhatsapp);
            setQrModalOpen(true);
          }
        }, 5000);
      }
    } catch (error) {
      console.error("❌ Error reconectando WhatsApp:", error);
      // ✅ CERRAR MODAL DE PROGRESO EN CASO DE ERROR
      setReconnectModalOpen(false);
      setReconnectingWhatsApp(null);
      // ✅ MOSTRAR SOLO UNA NOTIFICACIÓN DE ERROR
      toast.error("❌ Error al reconectar WhatsApp. Intente nuevamente.");
    }
  };

  // ✅ FUNCIÓN ELIMINADA: handleReconnectComplete - La actualización automática ahora se maneja en el modal

  // ✅ FUNCIÓN PARA MANEJAR ÉXITO EN CREACIÓN/EDICIÓN (OPTIMIZADA)
  const handleWhatsAppSuccess = async () => {
    try {
      // ✅ Cerrar modal inmediatamente
      setWhatsAppModalOpen(false);
      
      // ✅ NOTIFICACIÓN DE ÉXITO ELIMINADA - Evitar spam
      
      // ✅ ACTUALIZACIÓN NO BLOQUEANTE - Usar setTimeout para no bloquear UI
      setTimeout(async () => {
        try {
          await refreshWhatsApps();
        } catch (error) {
          console.error("Error en actualización post-creación:", error);
        }
      }, 2000); // ✅ 2 segundos (menos agresivo)
      
    } catch (error) {
      console.error("Error al manejar éxito de WhatsApp:", error);
      toastError(error);
    }
  };

  // ✅ FUNCIÓN PARA MANEJAR ÉXITO EN ELIMINACIÓN (OPTIMIZADA)
  const handleDeleteSuccess = async (whatsAppId) => {
    try {
      // ✅ Cerrar modal de confirmación inmediatamente
      setConfirmModalOpen(false);
      setConfirmModalInfo(confirmationModalInitialState);
      
      // ✅ NOTIFICACIÓN DE ÉXITO ELIMINADA - Evitar spam
      
      // ✅ ACTUALIZACIÓN NO BLOQUEANTE - Usar setTimeout para no bloquear UI
      setTimeout(async () => {
        try {
          await refreshWhatsApps();
          console.log("ACTUALIZACIÓN POST-ELIMINACIÓN COMPLETADA");
        } catch (error) {
          console.error("Error en actualización post-eliminación:", error);
        }
      }, 2000); // ✅ 2 segundos (no bloqueante)
      
    } catch (error) {
      console.error("Error al manejar éxito de eliminación:", error);
      toastError(error);
    }
  };

	// ✅ FUNCIÓN ELIMINADA: handleStartWhatsAppSession - Ya no se usa después de eliminar botón "INTENTAR DE NUEVO"

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			const whatsapp = whatsApps.find(w => Number(w.id) === Number(whatsAppId));
			if (!whatsapp) {
				toast.error("❌ No se encontró la conexión WhatsApp");
				return;
			}
			
			// ✅ ABRIR MODAL PRIMERO (antes de solicitar QR para que esté listo para recibir eventos)
			setSelectedWhatsApp(whatsapp);
			setQrModalOpen(true);
			
			// ✅ SOLICITAR GENERACIÓN DE QR (después de abrir modal)
			// Esto permite que el modal esté listo para recibir los eventos de socket
			await api.post(`/whatsappsession/${whatsAppId}`);
			
			// ✅ REFRESCAR CONEXIONES EN SEGUNDO PLANO PARA ACTUALIZAR ESTADO
			setTimeout(async () => {
				try {
					await refreshWhatsApps();
				} catch (error) {
					console.error("Error actualizando estado:", error);
				}
			}, 1500);
			
		} catch (err) {
			console.error("Error al generar nuevo código QR:", err);
			toastError(err);
			// ✅ CERRAR MODAL SI HAY ERROR
			setQrModalOpen(false);
			setSelectedWhatsApp(null);
		}
	};

	const handleOpenWhatsAppModal = (type = "whatsapp") => {
		setConnectionType(type === "telegram" ? "telegram" : "whatsapp");
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
		setConnectionType("whatsapp");
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const pairFromDashboardRef = useRef(false);
	useEffect(() => {
		if (pairFromDashboardRef.current || loading) return;
		const params = new URLSearchParams(location.search);
		if (params.get("pair") !== "1") return;
		const whatsappId = Number(params.get("whatsappId"));
		if (!whatsappId || !whatsApps?.length) return;
		const whatsapp = whatsApps.find((item) => Number(item.id) === whatsappId);
		if (!whatsapp) return;
		pairFromDashboardRef.current = true;
		history.replace("/connections");
		handleRequestNewQrCode(whatsapp.id);
	}, [loading, whatsApps, location.search, history, handleRequestNewQrCode]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
			setConfirmModalOpen(true);
		}

		if (action === "delete") {
			// ✅ USAR NUEVO MODAL DE ELIMINACIÓN COMPLETA
			const whatsapp = whatsApps.find(w => w.id === whatsAppId);
			if (whatsapp) {
				setDeletingWhatsApp(whatsapp);
				setDeleteModalOpen(true);
			}
		}
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalOpen(false);
		setConfirmModalInfo(confirmationModalInitialState);
	};

	const handleDisconnectWhatsApp = async () => {
		try {
			console.log(`🔄 Iniciando desconexión para WhatsApp ${confirmModalInfo.whatsAppId}`);
			
			// ✅ ENCONTRAR EL WHATSAPP A DESCONECTAR
			const whatsapp = whatsApps.find(w => w.id === confirmModalInfo.whatsAppId);
			if (!whatsapp) {
				toast.error("❌ No se encontró la conexión WhatsApp");
				handleCloseConfirmModal();
				return;
			}
			
			// ✅ CERRAR MODAL DE CONFIRMACIÓN INMEDIATAMENTE
			handleCloseConfirmModal();
			
			// ✅ CONFIGURAR ESTADO PARA EL MODAL DE PROGRESO
			setDisconnectingWhatsApp(whatsapp);
			setDisconnectModalOpen(true);
			
      // ✅ DESCONECTAR WHATSAPP e indicar razón manual
      await api.put(`/whatsapp/${confirmModalInfo.whatsAppId}`, {
        status: "DISCONNECTED",
        lastDisconnectReason: "USER_DISCONNECT"
      });
			
			console.log("✅ Desconexión iniciada exitosamente");
			
			// ✅ ACTUALIZAR ESTADO LOCAL DESPUÉS DE UN DELAY
			setTimeout(async () => {
				try {
					await refreshWhatsApps();
					console.log("✅ Estado actualizado después de desconexión");
				} catch (error) {
					console.error("Error actualizando estado:", error);
				}
			}, 2000);
			
		} catch (err) {
			console.error("❌ Error desconectando WhatsApp:", err);
			// ✅ CERRAR MODAL DE PROGRESO EN CASO DE ERROR
			setDisconnectModalOpen(false);
			setDisconnectingWhatsApp(null);
			// ✅ MOSTRAR SOLO UNA NOTIFICACIÓN DE ERROR
			toast.error("❌ Error al desconectar WhatsApp. Intente nuevamente.");
		}
	};

	const handleDeleteWhatsApp = async () => {
		try {
			// ✅ ELIMINAR WHATSAPP - Eliminar completamente de la BD
			await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
			
			// ✅ USAR NUEVA FUNCIÓN DE ÉXITO
			await handleDeleteSuccess(confirmModalInfo.whatsAppId);
			
		} catch (err) {
			console.error("Error al eliminar WhatsApp:", err);
			toastError(err);
		}
	};

	// ✅ FUNCIÓN PARA ELIMINACIÓN COMPLETA CON PROGRESO
	const handleCompleteDelete = async (whatsAppId) => {
		try {
			console.log(`🗑️ Iniciando eliminación completa para WhatsApp ${whatsAppId}`);
			
			// ✅ LLAMAR API PARA ELIMINACIÓN COMPLETA
			const response = await api.delete(`/whatsapp/${whatsAppId}`);
			
			if (response.status === 200) {
				console.log("✅ Eliminación completa exitosa");
				
				// ✅ MOSTRAR ESTADÍSTICAS DE ELIMINACIÓN
				if (response.data.deletedStats) {
					const stats = response.data.deletedStats;
					console.log("📊 Estadísticas de eliminación:", stats);
					
					// ✅ MOSTRAR NOTIFICACIÓN CON ESTADÍSTICAS
					toast.success(
						`✅ Eliminación completa exitosa: ${stats.deletedTickets} tickets, ${stats.deletedContacts} contactos, ${stats.deletedMessages} mensajes, ${stats.deletedTicketTrackings} estadísticas, ${stats.deletedUserRatings} evaluaciones eliminados`
					);
				}
				
				return response.data; // Return complete response data
			}
		} catch (error) {
			console.error("❌ Error en eliminación completa:", error);
			toast.error("❌ Error al eliminar WhatsApp. Intente nuevamente.");
			throw error; // Re-throw to be caught by the modal's error handling
		}
	};

	// ✅ FUNCIÓN PARA MANEJAR COMPLETADO DE ELIMINACIÓN Y RECARGAR PÁGINA
	const handleDeleteComplete = () => {
		console.log("🔄 Eliminación completada, actualizando página...");
		window.location.reload();
	};

	const handleMenuOpen = (event, whatsApp) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedMenuWhatsApp(whatsApp);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedMenuWhatsApp(null);
	};

	const handleMenuEdit = () => {
		if (selectedMenuWhatsApp) {
			handleEditWhatsApp(selectedMenuWhatsApp);
		}
		handleMenuClose();
	};

	const handleMenuDelete = () => {
		if (selectedMenuWhatsApp) {
			handleOpenConfirmationModal("delete", selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const handleMenuRestart = () => {
		if (selectedMenuWhatsApp) {
			handleReconnectWhatsApp(selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const handleMenuDisconnect = () => {
		if (selectedMenuWhatsApp) {
			handleOpenConfirmationModal("disconnect", selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const handleMenuCleanSession = () => {
		if (selectedMenuWhatsApp) {
			handleCleanAndReconnect(selectedMenuWhatsApp.id);
		}
		handleMenuClose();
	};

	const getConnectionNumber = (whatsApp) => {
		const directNumber = String(whatsApp?.number || "").trim();
		if (directNumber) return directNumber;

		const instanceNumber = String(whatsApp?.instance || "")
			.split(":")[0]
			.replace(/\D/g, "");

		return instanceNumber || "-";
	};

	const getConnectionTitle = (whatsApp) => {
		const configuredName = String(whatsApp?.name || "").trim();
		const waName = String(whatsApp?.waName || "").trim();

		return configuredName || waName || "Conexión principal";
	};

	const getConfiguredName = (whatsApp) => {
		const configuredName = String(whatsApp?.name || "").trim();
		const waName = String(whatsApp?.waName || "").trim();

		if (!configuredName || configuredName === waName) return "";
		return configuredName;
	};

	const getConnectionAvatar = (whatsApp) => {
		const avatar = String(whatsApp?.avatar || "").trim();
		if (avatar && !avatar.includes("nopicture.png")) return avatar;
		return whatsApp?.type === "telegram"
			? "https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
			: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg";
	};

	const getPlatformLogo = (whatsApp) =>
		whatsApp?.type === "telegram"
			? "https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
			: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg";

	const getDisplayStatus = (whatsApp) => {
		if (whatsApp?.type === "telegram" && whatsApp?.status === "CONNECTED") {
			return "CONNECTED";
		}
		return whatsApp?.effectiveStatus || whatsApp?.status || "DISCONNECTED";
	};

	const formatDetailValue = (value) => {
		if (value === null || value === undefined || value === "") return "-";
		if (typeof value === "boolean") return value ? "Sí" : "No";
		if (Array.isArray(value)) {
			if (value.length === 0) return "-";
			return value
				.map((item) => {
					if (item && typeof item === "object") {
						return item.name || item.id || JSON.stringify(item);
					}
					return String(item);
				})
				.join(", ");
		}
		if (typeof value === "object") return JSON.stringify(value, null, 2);
		return String(value);
	};

	const getConnectionDetails = (whatsApp) => {
		if (!whatsApp) return [];

		const queueNames = Array.isArray(whatsApp.queues)
			? whatsApp.queues.map(queue => queue?.name).filter(Boolean).join(", ")
			: "";
		const automation = [
			whatsApp.promptId ? `Prompt ${whatsApp.promptId}` : "",
			whatsApp.integrationId ? `Integración ${whatsApp.integrationId}` : "",
			whatsApp.flowBuilderId ? `FlowBuilder ${whatsApp.flowBuilderId}` : "",
		].filter(Boolean).join(", ");

		return [
			["ID", whatsApp.id],
			["Canal", whatsApp.type === "telegram" ? "Telegram" : "WhatsApp"],
			["Número", getConnectionNumber(whatsApp)],
			["Nombre", whatsApp.waName],
			["Alias", getConfiguredName(whatsApp) || whatsApp.name],
			["Estado", getDisplayStatus(whatsApp)],
			["Departamento", queueNames],
			["Automatización", automation],
			["Motivo desconexión", whatsApp.lastDisconnectReason],
			["Predeterminada", whatsApp.isDefault],
			["Actualizada", whatsApp.updatedAt ? formatDateTime(whatsApp.updatedAt) : ""],
		].filter(([, value]) => value !== null && value !== undefined && value !== "");
	};

	const getStatusBadge = (whatsApp) => {
		const status = getDisplayStatus(whatsApp);
		if (status === "CONNECTED") {
			return (
				<Chip
					icon={<CheckCircle style={{ color: '#4caf50', fontSize: '16px' }} />}
					label="Conectada"
					className={`${classes.statusBadge} ${classes.connectedBadge}`}
					size="small"
				/>
			);
		} else if (status === "OPENING" || status === "qrcode" || status === "PENDING") {
			const label = status === "OPENING" ? "Abriendo" : "QR pendiente";
			return (
				<Chip
					label={label}
					className={`${classes.statusBadge} ${classes.pendingBadge}`}
					size="small"
				/>
			);
		} else if (status === "RECONNECTING" || status === "ATTENTION") {
			return (
				<Chip
					label={status === "RECONNECTING" ? "Reconectando" : "Revisar sesión"}
					className={`${classes.statusBadge} ${classes.pendingBadge}`}
					size="small"
				/>
			);
		} else {
			return (
				<Box display="flex" alignItems="center" style={{ marginTop: 8 }}>
					<Box
						style={{
							width: '8px',
							height: '8px',
							borderRadius: '50%',
							backgroundColor: '#f44336',
							marginRight: '6px',
						}}
					/>
					<Chip
						label="Desconectada"
						className={`${classes.statusBadge} ${classes.disconnectedBadge}`}
						size="small"
					/>
				</Box>
			);
		}
	};

	const getActionButtonText = (whatsApp) => {
		const status = getDisplayStatus(whatsApp);
		if (status === "qrcode") {
			return whatsApp?.type === "telegram" ? "Escanear Telegram" : "Nuevo QR-Code";
		} else if (status === "CONNECTED") {
			return "Editar";
		} else {
			return whatsApp?.type === "telegram" ? "Conectar Telegram" : "Nuevo QR-Code";
		}
	};

	const handleActionButtonClick = (whatsApp) => {
		const status = getDisplayStatus(whatsApp);
		if (status === "qrcode") {
			handleOpenQrModal(whatsApp);
		} else if (status === "CONNECTED") {
			handleEditWhatsApp(whatsApp);
		} else {
			handleRequestNewQrCode(whatsApp.id);
		}
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={handleCloseConfirmModal}
				onConfirm={confirmModalInfo.action === "disconnect" ? handleDisconnectWhatsApp : handleDeleteWhatsApp}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
				connectionType={selectedWhatsApp?.type}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				onSave={handleWhatsAppSuccess}
				connectionType={selectedWhatsApp?.type === "telegram" ? "telegram" : connectionType}
			/>
			<ReconnectProgressModal
				open={reconnectModalOpen}
				onClose={() => {
					setReconnectModalOpen(false);
					setReconnectingWhatsApp(null);
				}}
				whatsAppName={reconnectingWhatsApp?.name || "WhatsApp"}
				whatsAppId={reconnectingWhatsApp?.id}
			/>
			<DisconnectProgressModal
				open={disconnectModalOpen}
				onClose={() => {
					setDisconnectModalOpen(false);
					setDisconnectingWhatsApp(null);
				}}
				whatsAppName={disconnectingWhatsApp?.name || "WhatsApp"}
			/>
			<DeleteWhatsAppModal
				open={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false);
					setDeletingWhatsApp(null);
				}}
				whatsAppName={deletingWhatsApp?.name || "WhatsApp"}
				whatsAppId={deletingWhatsApp?.id}
				onConfirm={handleCompleteDelete}
				onDeleteComplete={handleDeleteComplete}
			/>
			<MainHeader>
				<Title>
					<span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						{i18n.t("connections.title")}
						<GuideOpenButton id="connection" title={i18n.t("connections.title")} to="/connections" startId="buttons" />
					</span>
				</Title>
				<MainHeaderButtonsWrapper>
					<Can
						role={user.profile}
						perform="connections-page:addConnection"
						yes={() => (
							<>
								<Box 
									position="relative" 
									display="inline-flex"
									style={{ 
										zIndex: 1300,
										position: 'relative'
									}}
								>
									<ButtonGroup 
										variant="contained" 
										color="primary"
										style={{
											borderRadius: '8px',
											boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
											overflow: 'visible',
											position: 'relative',
											zIndex: 1
										}}
									>
										<Button
											onClick={handleOpenWhatsAppModal}
											style={{ 
												textTransform: 'none', 
												fontWeight: 700,
												fontSize: '14px',
												padding: '8px 18px',
												borderRadius: 999,
												letterSpacing: 0,
											}}
										>
											Agregar
										</Button>
										<Button
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setAddMenuAnchorEl(e.currentTarget);
											}}
											style={{ 
												minWidth: '36px', 
												padding: '0 8px',
												borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
											}}
										>
											<ArrowDropDown />
										</Button>
									</ButtonGroup>
									<Menu
										anchorEl={addMenuAnchorEl}
										open={Boolean(addMenuAnchorEl)}
										onClose={() => setAddMenuAnchorEl(null)}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: 'right',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'right',
										}}
										style={{
											zIndex: 1301
										}}
										PaperProps={{
											style: {
												borderRadius: '8px',
												boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
												minWidth: '200px',
												marginTop: '4px',
												zIndex: 1301
											}
										}}
										MenuListProps={{
											style: {
												padding: '4px 0'
											}
										}}
										getContentAnchorEl={null}
									>
									<MenuItem 
										onClick={() => {
											handleOpenWhatsAppModal("whatsapp");
											setAddMenuAnchorEl(null);
										}}
										style={{
											padding: '12px 20px',
											fontSize: '14px'
										}}
									>
										<Box display="flex" alignItems="center" width="100%">
											<Box
												component="img"
												src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
												alt="WhatsApp"
												style={{
													width: 24,
													height: 24,
													marginRight: 12
												}}
											/>
											WhatsApp
										</Box>
									</MenuItem>
									<MenuItem 
										onClick={() => {
											handleOpenWhatsAppModal("telegram");
											setAddMenuAnchorEl(null);
										}}
										style={{
											padding: '12px 20px',
											fontSize: '14px'
										}}
									>
										<Box display="flex" alignItems="center" width="100%">
											<Box
												component="img"
												src="https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
												alt="Telegram"
												style={{
													width: 24,
													height: 24,
													marginRight: 12
												}}
											/>
											Telegram
										</Box>
									</MenuItem>
								</Menu>
								</Box>
							</>
						)}
					/>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper}>
				<Menu
					anchorEl={menuAnchorEl}
					open={Boolean(menuAnchorEl)}
					onClose={handleMenuClose}
				>
					<MenuItem onClick={handleMenuEdit}>
						<Edit style={{ marginRight: 8, fontSize: 18 }} />
						Editar
					</MenuItem>
					{/* ✅ OPCIÓN DESCONECTAR - Solo mostrar cuando la conexión está CONECTADA */}
					{selectedMenuWhatsApp && getDisplayStatus(selectedMenuWhatsApp) === "CONNECTED" && (
						<MenuItem onClick={handleMenuDisconnect}>
							<LinkOff style={{ marginRight: 8, fontSize: 18, color: '#f44336' }} />
							<span style={{ color: '#f44336' }}>Desconectar</span>
						</MenuItem>
					)}
					<MenuItem onClick={handleMenuDelete}>
						<DeleteOutline style={{ marginRight: 8, fontSize: 18 }} />
						Borrar
					</MenuItem>
					<MenuItem onClick={handleMenuRestart}>
						<Refresh style={{ marginRight: 8, fontSize: 18 }} />
						Reiniciar
					</MenuItem>
					{/* ✅ FASE 1C: OPCIÓN PARA LIMPIAR SESIÓN CORRUPTA - Mostrar si hay badSession o está desconectada */}
					{selectedMenuWhatsApp && 
					 (selectedMenuWhatsApp.lastDisconnectReason === "BAD_SESSION" ||
					  selectedMenuWhatsApp.lastDisconnectReason === "BAD_SESSION_CLEANUP_FAILED" ||
					  selectedMenuWhatsApp.lastDisconnectReason === "BAD_SESSION_CLEANED" ||
					  (getDisplayStatus(selectedMenuWhatsApp) === "DISCONNECTED" && selectedMenuWhatsApp.lastDisconnectReason)) && (
						<MenuItem onClick={handleMenuCleanSession}>
							<Build style={{ marginRight: 8, fontSize: 18, color: '#ff9800' }} />
							<span style={{ color: '#ff9800', fontWeight: 'bold' }}>Limpiar y Reconectar</span>
						</MenuItem>
					)}
				</Menu>
				{loading ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
						<Typography>Cargando conexiones...</Typography>
					</Box>
				) : whatsApps?.length > 0 ? (
					<Box className={classes.connectionsGrid}>
						{whatsApps.map(whatsApp => (
								<Box key={whatsApp.id}>
									<Paper className={`${classes.connectionCard}${getDisplayStatus(whatsApp) === "CONNECTED" ? ` ${classes.connectionCardConnected}` : ""}`}>
										<Box className={classes.cardHeader} display="flex" alignItems="flex-start">
											{/* ✅ NUEVO: Badge de conexión predeterminada */}
											{whatsApp.isDefault && (
												<Box className={classes.defaultBadge}>
													<Star className={classes.defaultIcon} />
													Predeterminada
												</Box>
											)}
													<Box className={classes.avatarWrap}>
														<Box
															component="img"
															src={getConnectionAvatar(whatsApp)}
															alt={getConnectionTitle(whatsApp)}
															className={classes.whatsappIcon}
															onError={(event) => {
																event.currentTarget.onerror = null;
																event.currentTarget.src = getPlatformLogo(whatsApp);
															}}
														/>
														<Box
															component="img"
															src={getPlatformLogo(whatsApp)}
															alt={whatsApp.type === "telegram" ? "Telegram" : "WhatsApp"}
															className={classes.platformBadge}
														/>
													</Box>
											<Box className={classes.connectionInfo}>
												<Typography className={classes.connectionName} title={getConnectionTitle(whatsApp)}>
													{getConnectionTitle(whatsApp)}
												</Typography>
												
												{/* ✅ Información completa de la conexión sin truncar */}
												<Box style={{ marginTop: theme.spacing(0.75) }}>
													{getConnectionDetails(whatsApp).map(([label, value]) => (
														<Box className={classes.connectionDetail} key={label}>
															<span className={classes.detailLabel}>{label}:</span>
															<span className={classes.detailValue}>
																{formatDetailValue(value)}
															</span>
														</Box>
													))}
												</Box>
												
												<Box style={{ marginTop: theme.spacing(0.75) }}>
													{getStatusBadge(whatsApp)}
												</Box>
											</Box>
											<Can
												role={user.profile}
												perform="connections-page:editOrDeleteConnection"
												yes={() => (
													<IconButton
														className={classes.menuButton}
														onClick={(e) => handleMenuOpen(e, whatsApp)}
														size="small"
													>
														<MoreVert />
													</IconButton>
												)}
											/>
										</Box>
										<Box className={classes.cardBody}>
											<Can
												role={user.profile}
												perform="connections-page:actionButtons"
												yes={() => (
													<Button
														className={classes.actionButton}
														onClick={() => handleActionButtonClick(whatsApp)}
														endIcon={<ArrowForward />}
														fullWidth
													>
														{getActionButtonText(whatsApp)}
													</Button>
												)}
											/>
										</Box>
									</Paper>
								</Box>
							))}
					</Box>
				) : (
					<Box className={classes.emptyState}>
						<Box className={classes.emptyStack}>
							<Box className={classes.emptyCard} style={{ transform: "translateX(-50%) rotate(-10deg)", top: 18, background: "linear-gradient(180deg,#fff,#f3e8ff)" }} />
							<Box className={classes.emptyCard} style={{ transform: "translateX(-50%) rotate(8deg)", top: 8, background: "linear-gradient(180deg,#fff,#e8f8ee)" }} />
							<Box className={classes.emptyCard} style={{ transform: "translateX(-50%)", top: 28, background: "linear-gradient(180deg,#fff,#e8f1ff)" }} />
						</Box>
						<Typography className={classes.emptyTitle}>
							Agrega tu primera conexión
						</Typography>
						<Typography className={classes.emptySubtitle}>
							Configura una conexión para comenzar a enviar y recibir mensajes.
						</Typography>
					</Box>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Connections;
